/**
 * Formatting of Leena-2 80% intervals (p10–p90). Clef results carry them;
 * generative results do not, so every helper here treats a missing or
 * non-finite bound as "no interval" and the callers render nothing extra.
 *
 * Shown to team users only, behind the `leena2.showUncertainty` view setting
 * (see `useShowLeena2Uncertainty`).
 */
import type { Leena2Interval } from '@upright/common/model/output/scores/leena2-intervals';

export type NumberFormatter = (value: number) => string;

/** An interval from optional/nullable bounds, or undefined when either bound
 *  is missing or not a finite number. */
export function toLeena2Interval(
  p10: number | null | undefined,
  p90: number | null | undefined,
): Leena2Interval | undefined {
  if (p10 == null || p90 == null || !Number.isFinite(p10) || !Number.isFinite(p90)) return undefined;
  return { p10, p90 };
}

/** Compact number formatting for ¢/$ values that span many orders of magnitude
 *  (as the Leena-2 debugger shows CPDs): one decimal from 10 up, two from 0.01,
 *  exponential below. */
export function formatLeena2Number(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 10) return value.toFixed(1);
  if (abs >= 0.01) return value.toFixed(2);
  if (abs > 0) return value.toExponential(1);
  return '0';
}

/** "2.1–8.0" — the bounds formatted with `format`, joined by an en dash. */
export function formatLeena2Interval(interval: Leena2Interval, format: NumberFormatter = formatLeena2Number): string {
  return `${format(interval.p10)}–${format(interval.p90)}`;
}

/** "4.2 (2.1–8.0)", or just "4.2" without an interval. */
export function formatValueWithLeena2Interval(
  value: number,
  interval: Leena2Interval | undefined,
  format: NumberFormatter = formatLeena2Number,
): string {
  const formatted = format(value);
  return interval ? `${formatted} (${formatLeena2Interval(interval, format)})` : formatted;
}

/** Tooltip text explaining an interval. */
export const LEENA2_INTERVAL_TITLE = '80% interval (10th–90th percentile) of the Leena-2 estimate';
